import { createDocument, getAllData } from "apis/customApiHandler";
import Spinner from "components/Spinner";
import { Fragment, useCallback, useState } from "react";
import { BiCloudDownload } from "react-icons/bi";
import { useQuery } from "react-query";
import ReactSelect from "react-select";
import { TD, TH } from "styled/table";
import exportExcel from "utils/exportExcel";
import { Input, Select } from "styled";
import Modal from "../components/Modal";
import { Button } from "styled";
import getFormattedDate from "utils/helpers";
import toast from "react-hot-toast";

let allMonths = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

const contractTypeOptions = [
  { value: "All", label: "All" },
  { value: "CPKM", label: "CPKM" },
  { value: "NON CPKM", label: "NON CPKM" },
];

const WheelAlignmentInLastFourMonths = () => {
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [fleetMap, setFleetMap] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [KMFrom, setKMFrom] = useState("");
  const [KMTo, setKMTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contractType, setContractType] = useState("");

  const getData = async () => {
    setIsLoading(true);
    const dt = await createDocument("/report/wheelAlignmentInLastFourMonths", {
      ...(selectedFleets?.includes("all")
        ? {
            fleets: getFleetOptions()
              .filter((item) => item.value !== "all" && item.value !== "clear")
              .map((item) => item.value)
              .join(","),
          }
        : selectedFleets.length && {
            fleets: selectedFleets.map((fleet) => fleet).join(","),
          }),
      ...(selectedHubs?.includes("all")
        ? {
            hubs: getHubOptions()
              .filter((item) => item.value !== "all" && item.value !== "clear")
              .map((item) => item.value)
              .join(","),
          }
        : selectedHubs.length && {
            hubs: selectedHubs.map((hub) => hub).join(","),
          }),
      ...((KMFrom || KMTo) && { KMFrom, KMTo }),
      ...(dateFrom && dateTo && { dateFrom, dateTo }),
      ...(contractType && { contractType }),
    });
    // console.log(dt)
    setData(dt);
    setIsLoading(false);
  };

  const getDownloadData = async () => {
    setDownloading(true);

    try {
      const data = await createDocument(
        "/report/wheelAlignmentInLastFourMonths",
        {
          ...(selectedFleets?.includes("all")
            ? {
                fleets: getFleetOptions()
                  .filter(
                    (item) => item.value !== "all" && item.value !== "clear"
                  )
                  .map((item) => item.value)
                  .join(","),
              }
            : selectedFleets.length && {
                fleets: selectedFleets.map((fleet) => fleet).join(","),
              }),
          ...(selectedHubs?.includes("all")
            ? {
                hubs: getHubOptions()
                  .filter(
                    (item) => item.value !== "all" && item.value !== "clear"
                  )
                  .map((item) => item.value)
                  .join(","),
              }
            : selectedHubs.length && {
                hubs: selectedHubs.map((hub) => hub).join(","),
              }),
          ...((KMFrom || KMTo) && { KMFrom, KMTo }),
          ...(dateFrom && dateTo && { dateFrom, dateTo }),
          ...(contractType !== "" && {
            contractType,
          }),
        }
      );
      // console.log(dt)
      if (!data?.data || !data?.data?.totalRow) {
        toast.error("No data available to export");
        setDownloading(false);
        return;
      }

      let totalRow = {
        "S.N": "Total",
        "Total Alignments": data.data.totalRow.Total ?? 0,
        "CPKM": data.data.totalRow.CPKM ?? 0,
        "Service": data.data.totalRow.Service ?? 0,
        "Total Vehicles": data.data.totalRow.totalVehicles ?? 0,
      };
      let exportData = data?.data?.inspections?.map((x, idx) => {
        let monthData = {};
        data?.data?.dateRange?.map((month) => {
          const dt = `${allMonths[parseInt(month.split("-")[0])]}-${
            month.split("-")[1]
          }`;
          monthData = { ...monthData, [dt]: x[month]?.count ?? 0 };
          totalRow = { ...totalRow, [dt]: data.data.totalRow?.[month] ?? 0 };
        });
        return {
          "S.N": idx + 1,
          "Fleet Name": x?.fleet ? `${x.fleet} (${x.fleetCity || "--"})` : "--",
          ...monthData,
          "Total Alignments": x?.Total,
          "CPKM": x?.CPKM?.length ?? 0,
          "Service": x?.Service?.length ?? 0,
          "Total Vehicles": x?.totalVehicles ?? 0,
        };
      });

      exportData.push(totalRow);

      exportExcel(exportData, "wheel-alignment-summary", totalRow, 2);
    } catch (err) {
      toast.message(err?.message ?? "Something Went wrong");
    } finally {
      setDownloading(false);
    }
  };

  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData("/customer-fleet", {
      limit: 3000,
    })
  );
  const { data: allHubs } = useQuery([`/customer-hub`], () =>
    getAllData("/customer-hub", {
      limit: 3000,
    })
  );

  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Clear",
          value: "clear",
        },
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getHubOptions = useCallback(() => {
    if (allHubs && allHubs.data && allHubs.data.length) {
      return [
        ...(selectedFleets?.length
          ? [
              {
                label: "All",
                value: "all",
              },
            ]
          : []),
        ...(selectedFleets?.length
          ? [
              {
                label: "Clear",
                value: "clear",
              },
            ]
          : []),
        ...(selectedFleets?.includes("all")
          ? allHubs?.data?.length &&
            allHubs?.data?.map((hub) => {
              return {
                value: hub._id,
                label: hub.hubName,
              };
            })
          : allHubs?.data?.length &&
            allHubs?.data
              ?.filter((hub) => {
                const selectedFleetsVal = allFleets?.data?.filter((fleet) =>
                  selectedFleets?.includes(fleet._id)
                );

                return selectedFleetsVal?.some(
                  (fleet) => fleet.fleetCode === hub.fleetCode
                );
              })
              ?.map((hub) => {
                return {
                  value: hub._id,
                  label: hub.hubName,
                };
              })),
      ];
    }
  }, [allFleets, allHubs, selectedFleets]);

  const months = data?.dateRange;
  return (
    <div>
      <div className="w-full px-8 flex justify-center items-center gap-4">
        <label className="w-full py-3">
          Select Fleets
          <ReactSelect
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: "transparent",
                minWidth: "100%",
                "&:hover": {
                  borderColor: "rgb(34 197 94)",
                },
              }),
            }}
            className="shadow-md rounded-md py-1 mb-2"
            options={getFleetOptions()}
            value={
              selectedFleets &&
              selectedFleets?.map((fleet) => {
                return {
                  value: fleet,
                  label: getFleetOptions()?.find(
                    (item) => item?.value === fleet
                  )?.label,
                };
              })
            }
            onChange={(value) => {
              let allValues = value?.map((item) => item.value);
              if (allValues?.includes("clear")) {
                // console.log(allValues)
                setSelectedFleets([]);
                setSelectedHubs([]);
                return;
              }
              if (
                !selectedFleets?.includes("all") &&
                allValues.includes("all")
              ) {
                setSelectedFleets(["all"]);
                return;
              }

              if (selectedFleets.includes("all")) {
                // console.log("first");
                // console.log(getFleetOptions())
                value = value.filter((item) => item.value !== "all");
              }

              let selectedFleetsVal = allFleets?.data?.filter((fleet) =>
                value?.map((item) => item.value)?.includes(fleet._id)
              );

              let selectedHubsVal = allHubs?.data?.filter((hub) =>
                selectedHubs?.map((item) => item)?.includes(hub._id)
              );

              setSelectedHubs(
                selectedHubsVal
                  ?.filter((hub) =>
                    selectedFleetsVal?.find(
                      (fleet) => fleet.fleetCode === hub.fleetCode
                    )
                  )
                  .map((hub) => hub._id)
              );

              // console.log({allHubs, selectedHubs})

              setSelectedFleets(value.map((fleet) => fleet.value));
            }}
            name="fleets"
            isMulti={true}
          />
        </label>
        <label className="w-full py-3">
          Select Hubs
          <ReactSelect
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: "transparent",
                minWidth: "100%",
                "&:hover": {
                  borderColor: "rgb(34 197 94)",
                },
              }),
            }}
            className="shadow-md rounded-md py-1 mb-2"
            options={getHubOptions()}
            value={
              selectedHubs &&
              selectedHubs?.map((hub) => {
                // console.log({hub})
                return {
                  value: hub,
                  label: getHubOptions()?.find((item) => item.value === hub)
                    ?.label,
                };
              })
            }
            onChange={(value) => {
              const allValues = value?.map((item) => item.value);
              // console.log({allValues})
              if (allValues.includes("clear")) {
                setSelectedHubs([]);
                return;
              }
              if (!selectedHubs?.includes("all") && allValues.includes("all")) {
                let selectedFleetCodes = allFleets?.data
                  ?.filter((fleet) => selectedFleets?.includes(fleet._id))
                  ?.map((fleet) => fleet.fleetCode);

                const selectedHubsData = allHubs?.data?.filter((hub) =>
                  selectedFleetCodes?.includes(hub.fleetCode)
                );

                const mp = fleetMap;
                selectedHubsData.map((hub) => {
                  if (selectedFleetCodes?.includes(hub.fleetCode)) {
                    if (mp[hub.fleetCode] === undefined) mp[hub.fleetCode] = [];
                    mp[hub.fleetCode] = [...mp[hub.fleetCode], hub._id];
                  }
                });

                setFleetMap(mp);
                setSelectedHubs(["all"]);
              } else {
                // console.log({value})
                value = value.filter((value) => value.value !== "all");
                // console.log({value})
                let selectedFleetCodes = allFleets?.data
                  ?.filter((fleet) => selectedFleets?.includes(fleet._id))
                  ?.map((fleet) => fleet.fleetCode);
                // console.log({allValues})

                const selectedHubsData = allHubs?.data?.filter((hub) =>
                  allValues?.includes(hub._id)
                );
                const mp = {};
                selectedHubsData.map((hub) => {
                  mp[hub.fleetCode] = [];
                });

                selectedHubsData.map((hub) => {
                  if (selectedFleetCodes?.includes(hub.fleetCode)) {
                    if (mp[hub.fleetCode] === undefined) mp[hub.fleetCode] = [];
                    mp[hub.fleetCode] = [...mp[hub.fleetCode], hub._id];
                  }
                });

                setFleetMap(mp);
                setSelectedHubs(value.map((hub) => hub.value));
              }
            }}
            name="hubs"
            isMulti={true}
          />
        </label>

        <label className="w-full py-3">
          Contract Type
          <ReactSelect
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: "transparent",
                minWidth: "100%",
                "&:hover": {
                  borderColor: "rgb(34 197 94)",
                },
              }),
            }}
            name="contractType"
            className="shadow-md rounded-md py-1 mb-2"
            value={contractTypeOptions.find(
              (option) => option.value === contractType
            )} // Find the option object based on the value
            onChange={(option) => {
              setContractType(option.value); // Store only the value
            }}
            options={contractTypeOptions}
          />
        </label>
      </div>
      <div className="w-full px-8 flex justify-start items-center gap-4">
        <label>
          Start Date{" "}
          <Input
            type="date"
            max={getFormattedDate()}
            name="dateFrom"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label>
          End Date{" "}
          <Input
            type="date"
            min={getFormattedDate(dateFrom)}
            name="dateTo"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label>
          From KM{" "}
          <Input
            type="number"
            name="KMFrom"
            value={KMFrom}
            onChange={(e) => setKMFrom(e.target.value)}
          />
        </label>
        <label>
          To KM{" "}
          <Input
            type="number"
            name="KMTo"
            value={KMTo}
            onChange={(e) => setKMTo(e.target.value)}
          />
        </label>

        <Button
          className="mt-5"
          type="button"
          bgPrimary
          disabled={selectedFleets.length == 0 || selectedHubs.length === 0}
          onClick={() => getData()}
        >
          Search
        </Button>
        {downloading
          ? "Downloading..."
          : selectedHubs.length !== 0 && (
              <BiCloudDownload
                className="w-10 h-10 mt-5 text-green-500 hover:scale-105 cursor-pointer text-center"
                onClick={() => {
                  getDownloadData();
                }}
              />
            )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                {data && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <TH scope="col">S.N.</TH>
                        <TH scope="col">Fleet Name</TH>
                        {data &&
                          data?.data?.dateRange.map((date) => (
                            <TH scope="col" className="text-center">{`${
                              allMonths[parseInt(date.split("-")[0])]
                            }-${date.split("-")[1]}`}</TH>
                          ))}
                        <TH scope="col" className="text-center">CPKM</TH>
                        <TH scope="col" className="text-center">Service</TH>
                        <TH scope="col" className="text-center">Total Vehicles</TH>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data &&
                        data.data &&
                        data.data.inspections &&
                        data.data.inspections?.map((d, idx) => (
                          <ViewList
                            data={d}
                            idx={idx}
                            months={data.data.dateRange}
                          />
                        ))}
                    </tbody>
                    <thead>
                      <tr className="text-gray-600 font-semibold bg-green-100">
                        <TD></TD>
                        <TD>TOTAL</TD>
                        {data &&
                          data?.data?.dateRange.map((date) => (
                            <TD scope="col" className="text-left">{`${
                              data?.data?.totalRow?.[date] ?? 0
                            }`}</TD>
                          ))}
                        <TD scope="col" className="text-center">{`${
                          data?.data?.totalRow?.CPKM ?? 0
                        }`}</TD>
                        <TD scope="col" className="text-center">{`${
                          data?.data?.totalRow?.Service ?? 0
                        }`}</TD>
                        <TD scope="col" className="text-center">{`${
                          data?.data?.totalRow?.totalVehicles ?? 0
                        }`}</TD>
                      </tr>
                    </thead>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WheelAlignmentInLastFourMonths;

const ViewList = ({ data, idx, months }) => {
  console.log("data--", data);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const modalHandler = useCallback(
    (month) => {
      if (month) {
        setModalData({
          data: data[month]?.vehicles,
          fleet: data.fleet,
          title:
            "Wheel Alignment Details " +
            `${data.fleet} (${data.fleetCity || "--"})` +
            " " +
            month,
        });
        setShowModal(true);
      } else {
        setShowModal(false);
        setModalData([]);
      }
    },
    [showModal, modalData, data]
  );
  return (
    <Fragment>
      {showModal && (
        <Modal
          handleClick={() => setShowModal(false)}
          title={modalData.title}
          className="max-w-2xl"
        >
          {console.log({ modalData })}
          {/* <h1>This is the modal</h1> */}
          <h1 className="w-full flex justify-center text-end px-2 my-1">
            <Button
              onClick={() => {
                exportExcel(
                  modalData.data?.map((x, idx) => ({
                    "S.N": idx + 1,
                    "Fleet Name": data?.fleet
                      ? `${data.fleet} (${data.fleetCity || "--"})`
                      : "--",
                    "Vehicle Reg No.": x?.registrationNumber,
                    "Inspection Date": x?.inspectionDate.replace(/-/g, "/"),
                    "Killometer Travelled": x?.killometerTravelled,
                    "Killometer Travelled (Before)": x?.killometerTravelled,
                    "Total TOE (Before)": x?.wheelAlignment?.totalTOE.before,
                    "Total TOE (After)": x?.wheelAlignment?.totalTOE.after,
                    "Thrust angle (Before)":
                      x?.wheelAlignment?.thrustAngle.before,
                    "Thrust angle (after)":
                      x?.wheelAlignment?.thrustAngle.after,
                    "Scrub angle (Before)":
                      x?.wheelAlignment?.scrubAngle.before,
                    "Scrub angle (After)": x?.wheelAlignment?.scrubAngle.after,
                  })),
                  modalData.title.split(" ").join("_") + "_Data"
                );
              }}
              className=" text-blue-500 hover:scale-105 cursor-pointer text-right"
            >
              Download
            </Button>
          </h1>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TH scope="col">S.N.</TH>
                <TH scope="col">Inspection Date</TH>
                <TH scope="col">Fleet Name</TH>
                <TH scope="col">Vehicle Number</TH>
                <TH scope="col">Killometer Covered</TH>
                {/* <TH scope="col">Before Alignment Date</TH> */}
                {/* <TH scope="col">Aligment date</TH>	 */}
                <TH scope="col">Total toe (Before)</TH>
                <TH scope="col">Total toe (After)</TH>
                <TH scope="col">Thrust angle (Before)</TH>
                <TH scope="col">Thrust angle (after)</TH>
                <TH scope="col">Scrub angle (Before)</TH>
                <TH scope="col">Scrub angle (After)</TH>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {modalData &&
                modalData.data?.length > 0 &&
                modalData.data.map((vehicle, index) => (
                  <tr>
                    <TD>{index + 1}</TD>
                    <TD>{vehicle?.inspectionDate}</TD>
                    <TD>
                      {modalData.fleet
                        ? `${modalData.fleet} (${data.fleetCity || "--"})`
                        : "--"}
                    </TD>
                    <TD>{vehicle?.registrationNumber}</TD>
                    <TD>{vehicle?.killometerTravelled}</TD>
                    {/* <TD>{vehicle?.killometerTravelled}</TD> */}
                    <TD>{vehicle?.wheelAlignment?.totalTOE?.before}</TD>
                    <TD>{vehicle?.wheelAlignment?.totalTOE?.after}</TD>
                    <TD>{vehicle?.wheelAlignment?.thrustAngle?.before}</TD>
                    <TD>{vehicle?.wheelAlignment?.thrustAngle?.after}</TD>
                    <TD>{vehicle?.wheelAlignment?.scrubAngle?.before}</TD>
                    <TD>{vehicle?.wheelAlignment?.scrubAngle?.after}</TD>
                  </tr>
                ))}
            </tbody>
          </table>
        </Modal>
      )}
      <tr className={`${idx % 2 !== 0 && "bg-gray-100"}`}>
        <TD>{idx + 1}</TD>
        <TD>
          {data?.fleet ? `${data.fleet} (${data.fleetCity || "--"})` : "--"}
        </TD>
        {months &&
          months.length > 0 &&
          months.map((month) => (
            <TD>
              <div className="flex gap-2 items-center justify-start">
                <span
                  className="hover:text-green-500 cursor-pointer"
                  onClick={() => modalHandler(month)}
                >
                  {data[month]?.count ?? 0}
                </span>
              </div>
            </TD>
          ))}
        <TD className="text-center">{data?.CPKM?.length ?? 0}</TD>
        <TD className="text-center">{data?.Service?.length ?? 0}</TD>
        <TD className="text-center">{data?.totalVehicles ?? 0}</TD>
      </tr>
    </Fragment>
  );
};
